/** Shared R3F canvas defaults: cap Retina fill rate and directional shadow map cost. */
export const VIEWPORT_MAX_DPR = 2;
export const VIEWPORT_SHADOW_MAP = 1024;

export function getViewportCappedDpr() {
  return typeof window !== "undefined"
    ? Math.min(window.devicePixelRatio || 1, VIEWPORT_MAX_DPR)
    : 1;
}

function isDarkModeFromDocument() {
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  const schemeAttr =
    root?.getAttribute("data-mantine-color-scheme") ||
    root?.dataset?.mantineColorScheme ||
    "";
  if (schemeAttr === "dark") return true;
  if (schemeAttr === "light") return false;
  if (root?.classList?.contains("dark")) return true;
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }
  return false;
}

function parseCssColorToRgb(input) {
  if (!input || typeof input !== "string") return null;
  const s = input.trim();
  const hex = s.startsWith("#") ? s.slice(1) : s;
  const core =
    hex.length >= 8
      ? hex.slice(0, 6)
      : hex.length === 6
        ? hex
        : hex.length === 3
          ? hex
            .split("")
            .map((c) => c + c)
            .join("")
          : null;
  if (!core || !/^[0-9a-fA-F]{6}$/.test(core)) return null;
  const n = parseInt(core, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Preview: outline when hovering objects linked to another artboard (matches selected layer row). */
export const PREVIEW_DESTINATION_OUTLINE_COLOR = "#fe9a00";

/** Canvas clear color — warm stone to harmonize with pro-tool chrome. */
export const VIEWPORT_SCENE_BACKGROUND = "#F5F3F0";

/** Gradient sky: soft neutral at horizon → white above (`MinimalGradientSky`). */
export const VIEWPORT_SKY_GRADIENT_BOTTOM = "#F5F3F0";
export const VIEWPORT_SKY_GRADIENT_TOP = "#FFFFFF";

// export const VIEWPORT_MODEL_EDGE_COLOR = "#B2BEB5";
export const VIEWPORT_MODEL_EDGE_COLOR = "#868E96";

const VIEWPORT_SCENE_THEME_LIGHT = {
  mode: "light",
  background: VIEWPORT_SCENE_BACKGROUND,
  skyBottom: VIEWPORT_SKY_GRADIENT_BOTTOM,
  skyHorizon: "#F5F3F0",
  skySunGlow: "#FFFFFF",
  skyTop: VIEWPORT_SKY_GRADIENT_TOP,
  floorDefault: "#ede7d9",
  edgeColor: VIEWPORT_MODEL_EDGE_COLOR,
  ambientIntensity: 1.5,
  directionalIntensity: 3,
  directionalColor: "#ffffff",
  previewPresenceDirectionalIntensity: 0.42,
  previewPresenceDirectionalColor: "#e8eaef",
  skyAtmosphere: {
    turbidity: 4.2,
    rayleigh: 1.6,
    mieCoefficient: 0.002,
    mieDirectionalG: 0.78,
    elevation: 42,
    azimuth: 180,
  },
};

const VIEWPORT_SCENE_THEME_DARK = {
  mode: "dark",
  background: "#132246",
  skyBottom: "#F08A45",
  skyHorizon: "#4B3D60",
  skySunGlow: "#FFD56A",
  skyTop: "#0A1A3F",
  floorDefault: "#1f2937",
  edgeColor: "#aeb7c7",
  ambientIntensity: 0.58,
  directionalIntensity: 0.5,
  directionalColor: "#FC9C54",
  previewPresenceDirectionalIntensity: 0.36,
  previewPresenceDirectionalColor: "#FD5E53",
  skyAtmosphere: {
    turbidity: 7.2,
    rayleigh: 2,
    mieCoefficient: 0.006,
    mieDirectionalG: 0.72,
    elevation: 2,
    azimuth: 90,
  },
};

export function getViewportSceneTheme(themeOverride) {
  if (themeOverride === "dark") return VIEWPORT_SCENE_THEME_DARK;
  if (themeOverride === "light") return VIEWPORT_SCENE_THEME_LIGHT;
  return isDarkModeFromDocument()
    ? VIEWPORT_SCENE_THEME_DARK
    : VIEWPORT_SCENE_THEME_LIGHT;
}

/**
 * Light “studio” scene (sky, floor default, lights) for cases that should ignore UI theme.
 * Live grid previews use `getViewportSceneTheme(viewportThemeOverride)` to match the editor.
 */
export function getThumbnailGridSceneTheme() {
  return VIEWPORT_SCENE_THEME_LIGHT;
}

/**
 * CSS variables on `:root` for screenshot / small preview canvases (read by tooling or future CSS).
 * Kept in sync with `getViewportSceneTheme` when viewport UI theme toggles.
 */
export function applyViewportSceneCssVars(themeOverride) {
  if (typeof document === "undefined") return;
  const t = getViewportSceneTheme(themeOverride);
  const root = document.documentElement;
  root.style.setProperty("--mbx-viewport-scene-background", t.background);
  root.style.setProperty("--mbx-viewport-scene-sky-top", t.skyTop);
  root.style.setProperty("--mbx-viewport-scene-sky-bottom", t.skyBottom);
  root.style.setProperty("--mbx-viewport-scene-sky-horizon", t.skyHorizon);
  root.style.setProperty("--mbx-viewport-scene-sky-sun-glow", t.skySunGlow);
  root.style.setProperty("--mbx-viewport-scene-ambient-intensity", String(t.ambientIntensity));
  root.style.setProperty("--mbx-viewport-scene-directional-intensity", String(t.directionalIntensity));
  root.style.setProperty("--mbx-viewport-scene-directional-color", t.directionalColor);
  root.style.setProperty("--mbx-viewport-scene-floor-fallback", t.floorDefault);
  root.style.setProperty("--mbx-viewport-scene-edge-color", t.edgeColor);
}

/**
 * @deprecated Legacy drei `<Sky />` props only. The editor uses `MinimalGradientSky` now.
 * Kept exported so older branches, partial HMR, or stray imports do not throw at runtime.
 */
export const VIEWPORT_SKY_ATMOSPHERE = {
  turbidity: 5,
  rayleigh: 1.65,
  mieCoefficient: 0.0024,
  mieDirectionalG: 0.78, 
};

/** Default floor colour (user-customisable in editor). */
export const VIEWPORT_DEFAULT_FLOOR_HEX = "#ede7d9";
/** Same as default floor, for zustand / API-style strings. */
export const VIEWPORT_DEFAULT_FLOOR_STORAGE = "#ede7d9";


/** GridHelper color1 = center lines, color2 = grid — tuned for contrast vs `floorColour`. */
export function getEditorGridLineColors(floorColour, mode = "light") {
  if (mode === "dark") {
    return { centerLine: "#6b5b4a", grid: "#374151" };
    // return { centerLine: "#6b5b4a", grid: "#374151" };
  }
  const rgb = parseCssColorToRgb(floorColour);
  const lum = rgb
    ? (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255
    : 0.92;
  if (lum > 0.52) {
    // Clean minimal: subtle grid (#E5E7EB) + slightly stronger axes for depth.
    return { centerLine: "#cfc8b4", grid: "#cfc8b4" };
    // return { centerLine: "#D1D5DB", grid: "#E5E7EB" };
  }
  return { centerLine: "#d8dce8", grid: "#98a3b3" };
}
